import { captureException } from "@sentry/core";
import {
  useContext,
  useEffect,
  useState,
  useMemo,
  ReactNode,
  createContext,
  useCallback,
} from "react";
import { roleName } from "./admin/shared/admin-utils";
import { ApiError, useApiClient } from "./api/ApiClient";
import { useContractParamsContext } from "./contract/ContractParams";
import { TITLE_POSTFIX, useDocumentTitle } from "./shared/DocumentTitle";
import { useSentryTag } from "./useSentryTag";
import { Cancelable, Cancellation, makeCancelable } from "./utils";
import { useTrackContext, useTracking } from "./Tracking";
import { contractTypeName } from "./contract/ContractContext";
import { ContractViewModel, Role } from "./contract/model/ContractViewModel";
import { useHistory } from "react-router-dom";
import { Dispatch } from "react";
import { SetStateAction } from "react";
import { FlowNoPreviewLoadingScreen } from "./shared/LoadingScreen";

export function getContractTitle(contract: ContractViewModel | null) {
  if (!contract) return null;

  if (contract.licensePlateNumber) return contract.licensePlateNumber;
  if (contract.vehicleData) {
    const { manufactureName, modelName } = contract.vehicleData;
    return [manufactureName, modelName].filter((p) => !!p?.length).join(" ");
  }
  return null;
}

export type ContractResult =
  | {
      data: ContractViewModel;
      role: Role;
    }
  | { data: null; role: null };

export interface ILoadContractContext {
  backgroundRole: Role;
  contract: ContractResult | null;
  updateContract: Dispatch<SetStateAction<ContractViewModel>>;
  loadError: ApiError | null;
  contractLoaded: Date | null;
  clear(): void;
  refresh(): Promise<void>;
}

const LoadContractContext = createContext<ILoadContractContext | null>(null);

export function useLoadContractContext() {
  const context = useContext(LoadContractContext);
  if (!context) throw new Error("No LoadContractContext found!");
  return context;
}

export function LoadContract({ children }: { children: ReactNode }) {
  const { token } = useContractParamsContext();
  const [loading, setLoading] = useState(!!token);
  const [contract, setContract] = useState<ContractResult | null>(null);
  const updateContract = useCallback(
    (action: SetStateAction<ContractViewModel>) =>
      setContract((prevState: ContractResult | null) => {
        if (!prevState || !prevState.data) {
          throw new Error("Cannot update contract before it is loaded!");
        }
        return {
          ...prevState,
          data: typeof action === "function" ? action(prevState.data) : action,
        };
      }),
    [setContract]
  );
  const [contractLoaded, setContractLoaded] = useState<Date | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [redirect, setRedirect] = useState<string | null>(null);
  const client = useApiClient();
  const [backgroundRole, setBackgroundRole] = useState(Role.Seller);
  const tracking = useTracking();
  const onLoad = useCallback(
    function onLoad({ contract, role }) {
      tracking.contractLoad(contract, role);
      setBackgroundRole(role);
    },
    [tracking]
  );

  useSentryTag(
    "contractId",
    contract && contract.data ? contract.data.id : null
  );
  useSentryTag("role", contract ? roleName(contract.role) : null);
  useTrackContext("role", contract ? roleName(contract.role) : null);
  useSentryTag(
    "contractType",
    contractTypeName(contract?.data?.contractType?.current)
  );
  useTrackContext(
    "contractType",
    contractTypeName(contract?.data?.contractType?.current)
  );
  const documentTitle = useMemo(() => {
    if (!contract) return null;
    if (!contract.data) return null;
    return getContractTitle(contract.data);
  }, [contract]);
  useDocumentTitle(
    documentTitle ? `${documentTitle} ${TITLE_POSTFIX}` : undefined
  );
  const missingContract = !contract;

  const load = useCallback(() => {
    async function loadContract(promise: Promise<ContractViewModel>) {
      setLoading(true);
      try {
        const loaded = new Date();
        const contract = await promise;
        const role =
          contract && "buyerToken" in contract ? Role.Buyer : Role.Seller;
        setContract({ data: contract, role });
        setContractLoaded(loaded);
        if (contract) onLoad({ contract, role });
        setLoading(false);
      } catch (error: unknown) {
        let apiError = error as ApiError | Cancellation;
        if (!("isCanceled" in apiError)) {
          if (apiError.status === 409) {
            const conflictResult = apiError.result as { archived: boolean };
            const archived = conflictResult.archived;
            if (archived) {
              setRedirect(`/arkiv?token=${token}`);
            }
          } else if (apiError.status !== 400) {
            setLoadError(apiError);
          } else {
            setContract({ data: null, role: null });
          }
          setLoading(false);
          captureException(apiError);
        }
      }
    }
    let cancelablePromise: Cancelable<ContractViewModel> | null = null;
    if (!!token && missingContract) {
      cancelablePromise = makeCancelable(
        client.get<ContractViewModel>(`contract/token/${token}`)
      );
      loadContract(cancelablePromise.promise);
    } else {
      setLoading(false);
    }
    return cancelablePromise;
  }, [token, missingContract, client, onLoad]);

  useEffect(() => {
    const cancelable = load();
    return () => {
      if (cancelable) cancelable.cancel();
    };
  }, [load]);

  const refresh = useCallback(async () => {
    const cancelable = load();
    if (cancelable) await cancelable.promise;
  }, [load]);

  const clear = useCallback(() => {
    setContract(null);
    setRedirect(null);
    setLoadError(null);
  }, [setContract, setRedirect, setLoadError]);

  const history = useHistory();

  useEffect(() => {
    if (redirect) history.replace(redirect);
  }, [redirect, history]);

  if (loading)
    return <FlowNoPreviewLoadingScreen text="Henter kjøpekontrakt" />;

  return (
    <LoadContractContext.Provider
      value={{
        contract,
        updateContract,
        loadError,
        clear,
        backgroundRole,
        contractLoaded,
        refresh,
      }}
    >
      {children}
    </LoadContractContext.Provider>
  );
}
